import {
  Box,
  Button,
  Divider,
  Stepper as MuiStepper,
  Stack,
  Step,
  StepLabel,
} from '@mui/material';
import { type JSX, useState } from 'react';

import useAuth from 'portal/src/auth/useAuth';

import ConfirmCancelDialog from './forms/ConfirmCancelDialog';
import { ExtraButtonConfig } from './forms/FormButtonColumn';

export interface Step {
  disableNext?: boolean;
  extraButtons?: ExtraButtonConfig[];
  stepLabel: string;
  nextBtnCondition?: () => Promise<boolean> | boolean;
  view: JSX.Element;
}

/**
 * Stepper can manage its own state or be fully controlled.
 * When `currentStep` and `onStepChange` are provided, they will be used instead of internal state
 */
const Stepper = ({
  currentStep: currentStepProp,
  defaultStep = 0,
  isPending = false,
  isUpdate,
  handleBackAction,
  onStepChange,
  steps,
}: {
  currentStep?: number;
  defaultStep?: number;
  isPending?: boolean;
  isUpdate: boolean;
  handleBackAction?: () => void;
  onStepChange?: (setFn: (newStep: number) => number) => void;
  steps: Step[];
}) => {
  const { hasReadOnlyAccess } = useAuth();
  const isReadOnly = hasReadOnlyAccess();

  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [currentStepState, setCurrentStepState] = useState(defaultStep);

  const currentStep = currentStepProp ?? currentStepState;
  const setCurrentStep = (setFn: (newStep: number) => number) => {
    onStepChange ? onStepChange(setFn) : setCurrentStepState(setFn);
  };

  const currentStepConfig = steps[currentStep];
  const lastStepIndex = steps.length - 1;

  const isNextDisabled =
    isPending || currentStepConfig.disableNext || isReadOnly;

  const extraButtons = currentStepConfig.extraButtons
    ? currentStepConfig.extraButtons.map((button, index) => {
        const { color, isDisabled, onClick, variant } = button;

        return (
          <Button
            color={color ?? 'info'}
            fullWidth
            key={index}
            disabled={isPending || isDisabled}
            disableElevation
            onClick={onClick}
            variant={variant ?? 'outlined'}
          >
            {button.label}
          </Button>
        );
      })
    : undefined;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: '100%',
        backgroundColor: 'background.paper',
        borderRadius: 1,
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        height: '100%',
      }}
    >
      <MuiStepper activeStep={currentStep} alternativeLabel>
        {steps.map(({ stepLabel }, index) => (
          <Step key={index}>
            <StepLabel>{stepLabel}</StepLabel>
          </Step>
        ))}
      </MuiStepper>
      <Divider />
      <Box sx={{ display: 'flex', gap: 2 }}>
        {currentStepConfig.view}
        <Stack
          spacing={2}
          sx={{
            display: 'flex',
            alignItems: 'center',
            alignSelf: 'flex-start',
            position: 'sticky',
            top: 20,
            width: '100%',
            maxWidth: 200,
            minWidth: 100,
          }}
        >
          <Button
            color="secondary"
            disabled={isNextDisabled}
            fullWidth
            loading={isPending}
            onClick={async () => {
              if (currentStepConfig.nextBtnCondition) {
                const passesCondition =
                  await currentStepConfig.nextBtnCondition();
                if (!passesCondition) {
                  return;
                }
              }

              if (currentStep >= lastStepIndex) {
                return;
              }

              setCurrentStep((current: number) => current + 1);
            }}
            variant="contained"
            disableElevation
          >
            {lastStepIndex === currentStep
              ? isUpdate
                ? 'Update'
                : 'Save'
              : 'Next'}
          </Button>
          <Button
            color="secondary"
            disabled={currentStep === 0 || isPending || isReadOnly}
            fullWidth
            onClick={() => setCurrentStep((current: number) => current - 1)}
            variant="outlined"
          >
            Previous
          </Button>
          <Button
            color="info"
            disabled={isPending}
            fullWidth
            onClick={
              currentStep === 0 && currentStepConfig.disableNext
                ? handleBackAction
                : () => setShowCancelDialog(true)
            }
            variant="outlined"
          >
            {currentStep === 0 && currentStepConfig.disableNext
              ? 'Back'
              : 'Cancel'}
          </Button>
          {extraButtons && (
            <>
              <Divider sx={{ width: '100%' }} />
              {extraButtons}
            </>
          )}
        </Stack>
      </Box>
      <ConfirmCancelDialog
        showDialog={showCancelDialog}
        onConfirmCancel={handleBackAction}
        onKeepFormState={() => setShowCancelDialog(false)}
      />
    </Box>
  );
};

export default Stepper;
